import { authFetch } from "lib/api/authFetch";
import { normalizeApiMessage } from "lib/i18n/apiMessage";
import { BACKEND_URL } from "lib/utils/baseUrl";

const ALLOWED_FIELDS = new Set([
  "facility_type",
  "location",
  "facility_name",
  "part_name",
  "building_name",
  "floor",
  "building_id",
  "floor_id",
  "location_id",
]);

function normalizeApiMessageWithFallbackKey(
  payload,
  fallbackMessage,
  fallbackKey,
) {
  const normalized = normalizeApiMessage(payload, fallbackMessage);
  return {
    ...normalized,
    messageKey: /^(PROJECT_NODE_|INVALID_PROJECT_NODE_)/.test(payload?.code ?? "")
      ? "" : normalized.messageKey || fallbackKey || "",
  };
}

function normalizeFields(fields) {
  if (!fields || typeof fields !== "object" || Array.isArray(fields)) {
    return {};
  }

  return Object.fromEntries(
    Object.entries(fields)
      .filter(([key, value]) => ALLOWED_FIELDS.has(key) && value !== undefined)
      .map(([key, value]) => [
        key,
        value === null ? null : String(value).trim(),
      ]),
  );
}

export async function postUpdateFileMetadata(projectId, ids, fields) {
  const safeIds = Array.isArray(ids)
    ? Array.from(new Set(ids.map((id) => String(id ?? "").trim()).filter(Boolean)))
    : [];
  const safeFields = normalizeFields(fields);

  if (!projectId || safeIds.length < 1 || Object.keys(safeFields).length < 1) {
    return {
      success: false,
      ...normalizeApiMessageWithFallbackKey(
        { message: "Invalid file metadata update request." },
        "Invalid file metadata update request.",
        "api.project.file.metadata.update.validation.invalidRequest",
      ),
    };
  }

  try {
    const path = `${BACKEND_URL}/project/${projectId}/files/metadata`;
    const response = await authFetch(
      path,
      {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          ids: safeIds,
          fields: safeFields,
        }),
      },
      {
        refreshUrlIncludes: "auth/refresh",
      },
    );

    if (response.status === 401) {
      return {
        success: false,
        ...normalizeApiMessageWithFallbackKey(
          { message: "Session expired. Please sign in again." },
          "Session expired. Please sign in again.",
          "api.auth.token.expired",
        ),
      };
    }

    const result = await response.json().catch(() => null);
    if (!result) {
      return {
        success: false,
        ...normalizeApiMessageWithFallbackKey(
          { message: "Failed to parse server response." },
          "Failed to parse server response.",
          "api.common.serverError",
        ),
      };
    }

    if (!response.ok || result?.success !== true) {
      const fallbackKey =
        response.status === 404
          ? "api.project.file.metadata.update.notFound"
          : response.status === 400
            ? "api.project.file.metadata.update.validation.invalidRequest"
            : "api.project.file.metadata.update.failed";

      return {
        success: false,
        ...normalizeApiMessageWithFallbackKey(
          result,
          "File metadata update failed.",
          fallbackKey,
        ),
      };
    }

    return {
      success: true,
      data: result?.data ?? null,
      ...normalizeApiMessageWithFallbackKey(
        result,
        "File metadata updated successfully.",
        "api.project.file.metadata.update.success",
      ),
    };
  } catch (error) {
    console.error("postUpdateFileMetadata request failed:", error);
    return {
      success: false,
      ...normalizeApiMessageWithFallbackKey(
        { message: "Server request failed." },
        "Server request failed.",
        "api.common.networkError",
      ),
    };
  }
}
